import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppScope } from '@/core/AppScopeContext';
import { useAuth } from '@/core/AuthContext';

/**
 * Navigation items with their visibility rules.
 * This mirrors the structure in AppSidebar.tsx for consistency.
 */
const NAVIGATION_ITEMS = [
  { url: '/', operatorVisible: true, requiresAdmin: false },
  { url: '/factory-dashboard', operatorVisible: false, requiresAdmin: false },
  { url: '/ai-control', operatorVisible: false, requiresAdmin: false },
  { url: '/instructions', operatorVisible: true, requiresAdmin: false },
  { url: '/quality-checks', operatorVisible: true, requiresAdmin: false },
  { url: '/defects', operatorVisible: true, requiresAdmin: false },
  { url: '/shift-reports', operatorVisible: false, requiresAdmin: false },
  { url: '/performance', operatorVisible: false, requiresAdmin: false },
  { url: '/competency', operatorVisible: true, requiresAdmin: false },
  { url: '/8d-management', operatorVisible: false, requiresAdmin: false },
  { url: '/supplier-quality', operatorVisible: false, requiresAdmin: false },
  { url: '/audits', operatorVisible: false, requiresAdmin: false },
  { url: '/energy', operatorVisible: false, requiresAdmin: false },
  { url: '/document-assistant', operatorVisible: false, requiresAdmin: false },
];

const ADMIN_ITEMS = [
  { url: '/admin/users', requiresAdmin: true },
  { url: '/admin/factory-structure', requiresAdmin: true },
  { url: '/admin/uns-mapper', requiresAdmin: true },
];

/**
 * Hook that validates the current route when factory changes.
 * 
 * ARCHITECTURE:
 * - Waits for all permission queries to finish loading before making decisions
 * - Only redirects to dashboard if route is truly inaccessible after permissions are loaded
 * - Prevents race conditions where stale/loading permission data causes incorrect redirects
 */
export const useRouteValidation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { effectiveMode, hasPermission, scopeReady } = useAppScope();
  const { profile } = useAuth();
  
  const isAdmin = hasPermission('factory.admin');
  const showAdmin = profile?.is_super_admin || isAdmin;
  
  // Perform validation once scope and access are ready
  useEffect(() => {
    if (!scopeReady) return;

    const currentPath = location.pathname;
    
    // Check if current route is a navigation item
    const navItem = NAVIGATION_ITEMS.find(item => item.url === currentPath);
    const adminItem = ADMIN_ITEMS.find(item => currentPath.startsWith(item.url));
    
    // If it's an admin route
    if (adminItem) {
      if (!showAdmin) {
        // User doesn't have admin access in new factory - redirect to dashboard
        navigate('/', { replace: true });
      }
      return;
    }
    
    // If it's a regular navigation item
    if (navItem) {
      // Check if route is visible for effective mode
      if (effectiveMode === 'operator' && !navItem.operatorVisible) {
        // Route not available in operator mode - redirect to dashboard
        navigate('/', { replace: true });
      }
      return;
    }
    
    // For other routes (profile, detail pages, etc.) - no redirect
    
  }, [
    scopeReady,
    effectiveMode,
    showAdmin, 
    location.pathname, 
    navigate
  ]);
};
